import { useState } from "react";
import { UserPlus } from "lucide-react";
import {
  Button,
  Card,
  DataList,
  PageHeader,
  SelectField,
  Sheet,
  StatusBadge,
  TextField,
  type DataColumn,
} from "@nahal/ui";
import type { Member, OrganizationRole } from "@nahal/api-client";
import { useAddMember, useMembers, useRemoveMember, useOrganizationAccess } from "../../lib/queries";

const roles: Array<{ value: OrganizationRole; label: string }> = [
  { value: "MEMBER", label: "Member" },
  { value: "BILLING", label: "Billing" },
  { value: "ADMIN", label: "Admin" },
  { value: "OWNER", label: "Owner" },
];

export function MembersScreen() {
  const members = useMembers();
  const { canManage, isOwner } = useOrganizationAccess();
  const add = useAddMember();
  const remove = useRemoveMember();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrganizationRole>("MEMBER");

  const columns: Array<DataColumn<Member>> = [
    { key: "name", header: "Name", primary: true, cell: (row) => row.name },
    { key: "email", header: "Email", cell: (row) => row.email },
    { key: "role", header: "Role", cell: (row) => <StatusBadge status={row.role} tone="brand" /> },
  ];

  function submit() {
    if (!canManage || (role === "OWNER" && !isOwner)) return;
    add.mutate(
      { email, role },
      {
        onSuccess: () => {
          setOpen(false);
          setEmail("");
          setRole("MEMBER");
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        title="Members"
        description="Who can reach this organisation."
        actions={canManage ? (
          <Button variant="solid" icon={<UserPlus className="size-4" />} onClick={() => setOpen(true)}>
            Add member
          </Button>
        ) : undefined}
      />

      <Card>
        <DataList
          columns={columns}
          rows={members.data ?? []}
          rowKey={(row) => row.id}
          loading={members.isLoading}
          error={members.isError ? "Could not load members." : null}
          empty={{ title: "No members yet" }}
          rowActions={(row) => canManage && (isOwner || row.role !== "OWNER") ? (
            <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(row.id)}>
              Remove
            </Button>
          ) : null}
        />
      </Card>

      <Sheet
        open={open && canManage}
        title="Add a member"
        description="They need a Nahal Labs account already."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="solid" size="lg" onClick={submit} disabled={add.isPending || !email}>
              {add.isPending ? "Adding..." : "Add member"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <SelectField
            label="Role"
            options={roles.filter((option) => isOwner || option.value !== "OWNER")}
            value={role}
            onChange={(event) => setRole(event.target.value as OrganizationRole)}
          />
        </div>
      </Sheet>
    </>
  );
}
